import { inject, Injectable } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../store/app/app.selectors';
import { catchError, filter, first, map, Observable, of, tap, timeout } from 'rxjs';
import { SetAppStateFromSubscriptionGuard, SetSessionExpired } from '../../store/app/app.actions';

@Injectable({ providedIn: 'root' })
class SubscriptionGuard {
  readonly store = inject(Store);
  readonly router = inject(Router);

  canActivate(): Observable<boolean> {
    return this.store.select(AppSelectors.hasSubscription).pipe(
      tap((hasSubscription) => {
        if (hasSubscription === undefined) {
          this.store.dispatch(new SetAppStateFromSubscriptionGuard());
        }
      }),
      filter((hasSubscription) => hasSubscription !== undefined),
      first(),
      timeout(8000), // 8 second timeout to prevent hanging on failed API calls
      catchError(() => {
        // If the API call times out or fails, trigger session expired flow
        this.store.dispatch(new SetSessionExpired(true));
        return of(false);
      }),
      map((hasSubscription) => {
        if (!hasSubscription) {
          this.router.navigate(['sign-up', 'subscription-selection']);
        }
        return Boolean(hasSubscription);
      }),
    );
  }
}

@Injectable({ providedIn: 'root' })
class HasSubscriptionGuard {
  readonly store = inject(Store);
  readonly router = inject(Router);

  canActivate(): Observable<boolean> {
    return this.store.select(AppSelectors.hasSubscription).pipe(
      tap((hasSubscription) => {
        if (hasSubscription === undefined) {
          this.store.dispatch(new SetAppStateFromSubscriptionGuard());
        }
      }),
      filter((hasSubscription) => hasSubscription !== undefined),
      first(),
      timeout(8000), // 8 second timeout to prevent hanging on failed API calls
      catchError(() => {
        // If the API call times out or fails, trigger session expired flow
        this.store.dispatch(new SetSessionExpired(true));
        return of(false);
      }),
      map((hasSubscription) => {
        if (hasSubscription) {
          this.router.navigate(['sign-up', 'business-certificate']);
        }
        return !hasSubscription;
      }),
    );
  }
}

export const canActivateSubscription: CanActivateFn = () => {
  return inject(SubscriptionGuard).canActivate();
};

export const canActivateHasSubscription: CanActivateFn = () => {
  return inject(HasSubscriptionGuard).canActivate();
};
